// Detect shipping carrier from tracking number format and build the
// official live-tracking URL. Client-side only — no API needed.
// Add more patterns as needed.

const CARRIERS = [
  {
    key: "dhl-express",
    name: "DHL Express",
    color: "#FFCC00",
    dark: "#D40511",
    logo: "DHL",
    patterns: [
      // 10-digit DHL Express waybill (numeric only)
      /^\d{10}$/,
      // Some DHL express codes: JD... 14 digits
      /^JD\d{12}$/i,
    ],
    url: (n) => `https://www.dhl.com/en/express/tracking.html?AWB=${encodeURIComponent(n)}&brand=DHL`,
  },
  {
    key: "dhl-ecommerce",
    name: "DHL eCommerce",
    color: "#FFCC00",
    dark: "#D40511",
    logo: "DHL",
    patterns: [
      // DHL eCommerce prefixes
      /^(GM|LX|RX|SG|CN|US|PL|CA|IN|MY|KR|TH|JP|SI|HK|AU)[A-Z0-9]{6,}$/i,
      // Long numeric with GM/LX prefix
      /^\d{20,22}$/,
    ],
    url: (n) => `https://webtrack.dhlecommerce.com/orders?trackingNumber=${encodeURIComponent(n)}`,
  },
  {
    key: "usps",
    name: "USPS",
    color: "#004B87",
    dark: "#E31837",
    logo: "USPS",
    patterns: [
      // USPS Priority Mail Express / Registered / Certified etc — 22 digits
      /^(94|93|92|94|95|82)\d{20}$/,
      // Priority Mail Express International
      /^E[A-Z]\d{9}(US|CN)$/i,
      // Global Express Guaranteed
      /^82\d{8}$/,
      // Legacy Delivery/Signature Confirmation
      /^7\d{19}$/,
      // Registered mail
      /^R[A-Z]\d{9}(US|CN)$/i,
    ],
    url: (n) => `https://tools.usps.com/go/TrackConfirmAction?tLabels=${encodeURIComponent(n)}`,
  },
  {
    key: "ups",
    name: "UPS",
    color: "#351C15",
    dark: "#FFB500",
    logo: "UPS",
    patterns: [
      // Standard UPS: 1Z + 16 alphanumeric
      /^1Z[A-Z0-9]{16}$/i,
      // UPS Mail Innovations: T + 10 digits
      /^T\d{10}$/i,
      // UPS Ground: 9 digits
      /^\d{9}$/,
    ],
    url: (n) => `https://www.ups.com/track?tracknum=${encodeURIComponent(n)}`,
  },
  {
    key: "fedex",
    name: "FedEx",
    color: "#4D148C",
    dark: "#FF6600",
    logo: "FedEx",
    patterns: [
      // FedEx Express: 12 digits
      /^\d{12}$/,
      // FedEx Ground: 15 digits
      /^\d{15}$/,
      // FedEx SmartPost / Home Delivery: 20-22 digits (starts with 96)
      /^96\d{18,20}$/,
      // FedEx Ground 96: 22 digits
      /^\d{22}$/,
    ],
    url: (n) => `https://www.fedex.com/fedextrack/?tracknumbers=${encodeURIComponent(n)}`,
  },
  {
    key: "aramex",
    name: "Aramex",
    color: "#E4002B",
    dark: "#000000",
    logo: "Aramex",
    patterns: [/^\d{8,11}$/],
    url: (n) => `https://www.aramex.com/track/results?ShipmentNumber=${encodeURIComponent(n)}`,
  },
  {
    key: "royal-mail",
    name: "Royal Mail",
    color: "#CC0000",
    dark: "#000000",
    logo: "Royal Mail",
    patterns: [/^[A-Z]{2}\d{9}GB$/i],
    url: (n) => `https://www.royalmail.com/track-your-item#/tracking-results/${encodeURIComponent(n)}`,
  },
  {
    key: "canada-post",
    name: "Canada Post",
    color: "#EE1C25",
    dark: "#000000",
    logo: "Canada Post",
    patterns: [/^\d{16}$/],
    url: (n) => `https://www.canadapost-postescanada.ca/track-reperage/en#/details/${encodeURIComponent(n)}`,
  },
  {
    key: "china-post",
    name: "China Post / EMS",
    color: "#E3001B",
    dark: "#000000",
    logo: "China Post",
    patterns: [/^[A-Z]{2}\d{9}CN$/i],
    url: (n) => `https://track.chinapost.com.cn/?trackingNumber=${encodeURIComponent(n)}`,
  },
  {
    key: "australia-post",
    name: "Australia Post",
    color: "#DC1928",
    dark: "#000000",
    logo: "Australia Post",
    patterns: [/^[A-Z]{2}\d{9}AU$/i, /^\d{13}$/],
    url: (n) => `https://auspost.com.au/mypost/track/details/${encodeURIComponent(n)}`,
  },
];

const MULTI_CARRIER_FALLBACK = (n) =>
  `https://parcelsapp.com/en/tracking/${encodeURIComponent(n)}`;

export function detectCarrier(rawNumber) {
  if (!rawNumber) return null;
  const number = rawNumber.replace(/\s+/g, "").toUpperCase();
  const matches = [];
  for (const c of CARRIERS) {
    if (c.patterns.some((p) => p.test(number))) {
      matches.push(c);
    }
  }
  return {
    number,
    matches,
    fallbackUrl: MULTI_CARRIER_FALLBACK(number),
  };
}

export { CARRIERS, MULTI_CARRIER_FALLBACK };
